'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { PropertyCardRootContext } from './context/property-card-root.context';
import { propertyCardPhotoFallbackRecipe } from './styles/property-card-photo-fallback.styles';
import type { PropertyCardPhotoFallbackPropsInterface } from './types/property-card-photo-fallback.types';

/**
 * The fallback image to display in a `PropertyCard` when no property photo, Street View
 * nor satellite image is available.
 *
 * @since 8.63.0
 */
export const PropertyCardPhotoFallback = forwardRef<
  SVGSVGElement,
  PropertyCardPhotoFallbackPropsInterface
>((props: PropertyCardPhotoFallbackPropsInterface, forwardedRef) => {
  const { appearance } = useContext(PropertyCardRootContext);
  const {
    altText = 'No image available for this property',
    className,
    css: cssProp,
    ...rest
  } = props;

  const styles = css(propertyCardPhotoFallbackRecipe.raw({ appearance }), cssProp);

  return (
    <svg
      ref={forwardedRef}
      data-c11n-component="PropertyCard.PhotoFallback"
      data-c11n-version={__C11N_VERSION__}
      aria-label={altText}
      className={cx('c11n', styles, className)}
      viewBox="0 0 72 73"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <title>{altText}</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M63.765 16.3885L32.265 5.13851C31.5725 4.8836 30.799 4.98449 30.195 5.40851C29.5986 5.83313 29.2461 6.52139 29.25 7.25351V37.291L24.0075 32.5885C23.1511 31.8156 21.8489 31.8156 20.9925 32.5885L7.8975 44.3335C7.17223 44.9898 6.75589 45.9204 6.75 46.8985V65.146C6.75 66.7242 8.02935 68.0035 9.6075 68.0035H63C64.2426 68.0035 65.25 66.9961 65.25 65.7535V18.5035C65.2496 17.5562 64.6559 16.7106 63.765 16.3885ZM27 63.5035H18V54.5035C18 53.2609 19.0074 52.2535 20.25 52.2535H24.75C25.9926 52.2535 27 53.2609 27 54.5035V63.5035ZM45 52.2535C45 53.4961 43.9926 54.5035 42.75 54.5035C41.5074 54.5035 40.5 53.4961 40.5 52.2535V25.2535C40.5 24.0109 41.5074 23.0035 42.75 23.0035C43.9926 23.0035 45 24.0109 45 25.2535V52.2535ZM54 52.2535C54 53.4961 52.9926 54.5035 51.75 54.5035C50.5074 54.5035 49.5 53.4961 49.5 52.2535V29.7535C49.5 28.5109 50.5074 27.5035 51.75 27.5035C52.9926 27.5035 54 28.5109 54 29.7535V52.2535Z"
      />
    </svg>
  );
});

PropertyCardPhotoFallback.displayName = 'PropertyCard.PhotoFallback';
